import { API_BASE_URL } from "@/config";
import { validateToken } from '@/utils/auth';

export default {
  name: 'ThankYou',
  props: {
    workflow_id: { type: [String, Number], default: null },
    custom_title: { type: String, default: '' },
    custom_message: { type: String, default: '' },
    custom_additional_info: { type: String, default: '' }
  },
  data() {
    return {
      // Content
      title: 'Thank You!',
      message: 'You have successfully completed the verification process.',
      additional_info: 'We will be in contact with you shortly.',
      button_text: 'Go to Dashboard',
      
      // Component visibility
      components: {
        logo: { visible: true, order: 0 },
        icon: { visible: true, order: 1 },
        title: { visible: true, order: 2 },
        message: { visible: true, order: 3 },
        info: { visible: true, order: 4 },
        support: { visible: true, order: 5 }
      },
      
      // Style settings
      styles: {
        // Background
        background_type: 'gradient', // 'solid', 'gradient', 'image'
        background_color: '#f8fafc',
        gradient_from: '#f8fafc',
        gradient_to: '#e2e8f0',
        gradient_angle: 180,
        background_image: '',
        
        // Card
        card_background: '#ffffff',
        card_border_radius: 24,
        card_shadow: 'medium', // 'none', 'small', 'medium', 'large'
        card_padding: 48,
        
        // Typography
        title_size: 32,
        title_weight: 700,
        title_color: '#1e293b',
        message_size: 16,
        message_color: '#64748b',
        
        // Icon
        icon_style: 'circle', // 'circle', 'square', 'none'
        icon_size: 88,
        primary_color: '#10b981',
        
        // Buttons
        button_style: 'filled', // 'filled', 'outline', 'ghost'
        button_radius: 12
      },
      
      // Support options
      support_heading: 'Need help?',
      support_email: 'support@example.com',
      support_phone: '',
      support_whatsapp: '',
      
      // Builder state
      is_loading: true,
      isAdmin: false,
      builderOpen: false,
      activePanel: 'content', // 'content', 'style', 'layout'
      editingField: null,
      saveStatus: '',
      previewMode: false,
      previewViewport: 'desktop', // 'desktop', 'tablet', 'mobile'
      
      // Drag state
      draggedComponent: null
    };
  },
  computed: {
    effectiveWorkflowId() {
      // Use prop workflow_id (from route) if available, otherwise fall back to store
      return this.workflow_id || this.$store.getters.get_workflow_id;
    },
    sortedComponents() {
      return Object.entries(this.components)
        .filter(([, comp]) => comp.visible)
        .sort((a, b) => a[1].order - b[1].order)
        .map(([name]) => name);
    },
    backgroundStyle() {
      if (this.styles.background_type === 'solid') {
        return { background: this.styles.background_color };
      } else if (this.styles.background_type === 'gradient') {
        return {
          background: `linear-gradient(${this.styles.gradient_angle}deg, ${this.styles.gradient_from} 0%, ${this.styles.gradient_to} 100%)`
        };
      } else if (this.styles.background_type === 'image' && this.styles.background_image) {
        return {
          backgroundImage: `url(${this.styles.background_image})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        };
      }
      return {};
    },
    cardStyle() {
      const shadows = {
        none: 'none',
        small: '0 1px 3px rgba(0,0,0,0.1)',
        medium: '0 4px 6px -1px rgba(0,0,0,0.05), 0 10px 15px -3px rgba(0,0,0,0.08), 0 20px 25px -5px rgba(0,0,0,0.05)',
        large: '0 25px 50px -12px rgba(0,0,0,0.15)'
      };
      return {
        background: this.styles.card_background,
        borderRadius: `${this.styles.card_border_radius}px`,
        boxShadow: shadows[this.styles.card_shadow],
        '--card-padding': `${this.styles.card_padding}px`
      };
    },
    titleStyle() {
      return {
        fontSize: `${this.styles.title_size}px`,
        fontWeight: this.styles.title_weight,
        color: this.styles.title_color
      };
    },
    messageStyle() {
      return {
        fontSize: `${this.styles.message_size}px`,
        color: this.styles.message_color
      };
    },
    iconStyle() {
      return {
        '--primary-color': this.styles.primary_color,
        '--icon-size': `${this.styles.icon_size}px`
      };
    }
  },
  mounted() {
    if (!validateToken(this.$router)) return;
    
    // Check if user is admin - only admins can see the builder
    const userRole = this.$store.getters.get_user_role;
    const isAdminRole = userRole && (
      userRole.toLowerCase() === 'clientadmin' || 
      userRole.toLowerCase() === 'systemadmin'
    );
    this.isAdmin = isAdminRole;
    this.builderOpen = isAdminRole;
    
    this.load_client_configuration();
  },
  methods: {
    async load_client_configuration() {
      const token = localStorage.getItem('token');
      try {
        // Use customer endpoint for non-admins, admin endpoint for admins
        const baseEndpoint = this.isAdmin 
          ? 'get_thank_you_configuration' 
          : 'get_customer_thank_you_configuration';
        let url = `${API_BASE_URL}/verify_api/workflow_management_api/${baseEndpoint}/`;
        if (this.effectiveWorkflowId) {
          url += `?workflow_id=${this.effectiveWorkflowId}`;
        }
        const response = await fetch(
          url,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Token ${token}`
            }
          }
        );
        if (response.ok) {
          const data = await response.json();
          if (data.configuration) this.apply_configuration(data.configuration);
        }
      } catch (error) {
        console.error('Failed to load configuration:', error);
      } finally {
        if (this.custom_title) this.title = this.custom_title;
        if (this.custom_message) this.message = this.custom_message;
        if (this.custom_additional_info) this.additional_info = this.custom_additional_info;
        this.is_loading = false;
      }
    },

    apply_configuration(config) {
      // Content
      if (config.title) this.title = config.title;
      if (config.message) this.message = config.message;
      if (config.additional_info) this.additional_info = config.additional_info;
      
      // Components
      if (config.components) this.components = { ...this.components, ...config.components };
      
      // Styles
      if (config.styles) this.styles = { ...this.styles, ...config.styles };
      
      // Support options
      if (config.support_heading) this.support_heading = config.support_heading;
      if (config.support_email) this.support_email = config.support_email;
      if (config.support_phone) this.support_phone = config.support_phone;
      if (config.support_whatsapp) this.support_whatsapp = config.support_whatsapp;
    },

    toggleComponent(name) {
      this.components[name].visible = !this.components[name].visible;
      this.autoSave();
    },

    startEdit(field) {
      this.editingField = field;
    },

    finishEdit() {
      this.editingField = null;
      this.autoSave();
    },

    // Drag and drop reordering
    onDragStart(e, componentName) {
      this.draggedComponent = componentName;
      e.dataTransfer.effectAllowed = 'move';
    },

    onDragOver(e, componentName) {
      e.preventDefault();
      if (this.draggedComponent && this.draggedComponent !== componentName) {
        const draggedOrder = this.components[this.draggedComponent].order;
        const targetOrder = this.components[componentName].order;
        this.components[this.draggedComponent].order = targetOrder;
        this.components[componentName].order = draggedOrder;
      }
    },

    onDragEnd() {
      this.draggedComponent = null;
      this.autoSave();
    },

    moveComponent(name, direction) {
      const entries = Object.entries(this.components).sort((a, b) => a[1].order - b[1].order);
      const idx = entries.findIndex(([n]) => n === name);
      if (direction === 'up' && idx > 0) {
        const temp = entries[idx][1].order;
        entries[idx][1].order = entries[idx - 1][1].order;
        entries[idx - 1][1].order = temp;
      } else if (direction === 'down' && idx < entries.length - 1) {
        const temp = entries[idx][1].order;
        entries[idx][1].order = entries[idx + 1][1].order;
        entries[idx + 1][1].order = temp;
      }
      this.autoSave();
    },

    autoSave() {
      // Debounced auto-save
      clearTimeout(this._saveTimeout);
      this._saveTimeout = setTimeout(() => this.save_config(), 800);
    },

    async save_config() {
      this.saveStatus = 'saving';
      const token = localStorage.getItem('token');
      const payload = {
        workflow_id: this.effectiveWorkflowId,
        configuration: {
          title: this.title,
          message: this.message,
          additional_info: this.additional_info,
          support_heading: this.support_heading,
          support_email: this.support_email,
          support_phone: this.support_phone,
          support_whatsapp: this.support_whatsapp,
          components: this.components,
          styles: this.styles
        }
      };
      try {
        const response = await fetch(
          `${API_BASE_URL}/verify_api/workflow_management_api/save_thank_you_configuration/`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Token ${token}`
            },
            body: JSON.stringify(payload)
          }
        );
        if (response.ok) {
          this.saveStatus = 'saved';
          setTimeout(() => { this.saveStatus = ''; }, 2000);
        } else {
          throw new Error('Save failed');
        }
      } catch (e) {
        this.saveStatus = 'error';
        setTimeout(() => { this.saveStatus = ''; }, 3000);
      }
    },

    // Preset themes
    applyPreset(preset) {
      const presets = {
        minimal: {
          background_type: 'solid',
          background_color: '#ffffff',
          card_background: '#f8fafc',
          card_border_radius: 16,
          card_shadow: 'small',
          primary_color: '#1e293b',
          title_color: '#1e293b'
        },
        modern: {
          background_type: 'gradient',
          gradient_from: '#f8fafc',
          gradient_to: '#e2e8f0',
          card_background: '#ffffff',
          card_border_radius: 24,
          card_shadow: 'medium',
          primary_color: '#10b981',
          title_color: '#1e293b'
        },
        vibrant: {
          background_type: 'gradient',
          gradient_from: '#667eea',
          gradient_to: '#764ba2',
          card_background: 'rgba(255,255,255,0.95)',
          card_border_radius: 32,
          card_shadow: 'large',
          primary_color: '#8b5cf6',
          title_color: '#1e1b4b'
        },
        dark: {
          background_type: 'solid',
          background_color: '#0f172a',
          card_background: '#1e293b',
          card_border_radius: 20,
          card_shadow: 'large',
          primary_color: '#22d3ee',
          title_color: '#f1f5f9',
          message_color: '#94a3b8'
        }
      };
      if (presets[preset]) {
        this.styles = { ...this.styles, ...presets[preset] };
        this.autoSave();
      }
    },

    goToConfirmWorkflow() {
      // Save before navigating
      this.save_config().then(() => {
        this.$router.push({ name: 'confirm_workflow' });
      });
    }
  }
};
